import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { MapPin, Phone, Globe, Clock, BadgeCheck, Tag, Star, Store, ArrowLeft, Building2, UtensilsCrossed, Camera } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Spinner, Pill, vibrantTheme } from "../components/common";
import { BusinessCard, locationTrail } from "../components/AirportShops";
import PhotoGallery from "../components/PhotoGallery";
import { MinisiteView } from "../components/Minisite";
import { useAuth } from "../context/AuthContext";

function ClaimForm({ bizId, onDone }) {
  const [form, setForm] = useState({ contact_name: "", role: "", business_email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/marketplace/businesses/${bizId}/claim`, form);
      toast.success("Claim submitted — our team will verify and get back to you");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Claim failed"); }
    setBusy(false);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <form onSubmit={submit} className="space-y-3 mt-4" data-testid="claim-form">
      <input required minLength={2} placeholder="Your full name" value={form.contact_name} onChange={set("contact_name")} data-testid="claim-name-input" className="w-full border border-zinc-300 px-3 py-2 text-sm" />
      <input required minLength={2} placeholder="Your role (Owner, Manager, Franchisee…)" value={form.role} onChange={set("role")} data-testid="claim-role-input" className="w-full border border-zinc-300 px-3 py-2 text-sm" />
      <input required type="email" placeholder="Business email (used for verification)" value={form.business_email} onChange={set("business_email")} data-testid="claim-email-input" className="w-full border border-zinc-300 px-3 py-2 text-sm" />
      <textarea placeholder="Anything that helps us verify (optional)" value={form.message} onChange={set("message")} rows={3} className="w-full border border-zinc-300 px-3 py-2 text-sm" />
      <button disabled={busy} data-testid="claim-submit-btn" className="bg-emerald-700 text-white px-5 py-2.5 text-sm font-700 hover:bg-emerald-600 transition-colors disabled:opacity-50">
        {busy ? "Submitting…" : "Submit claim for verification"}
      </button>
    </form>
  );
}

function Reviews({ biz, reload }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const submit = async () => {
    try {
      await api.post(`/marketplace/businesses/${biz.id}/reviews`, { rating, text });
      toast.success("Review posted");
      setText(""); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to post review"); }
  };
  return (
    <div data-testid="business-reviews">
      <h2 className="font-heading font-900 text-xl mb-3 flex items-center gap-2">
        <Star className="w-5 h-5 text-amber-500" /> Traveler reviews
        {biz.avg_rating && <span className="text-sm font-500 text-muted-foreground">{biz.avg_rating} / 5 · {biz.review_count} review{biz.review_count === 1 ? "" : "s"}</span>}
      </h2>
      {user ? (
        <div className="border border-zinc-300 bg-white p-4 mb-4">
          <div className="flex items-center gap-1 mb-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setRating(n)} data-testid={`review-star-${n}`} aria-label={`${n} stars`}>
                <Star className={`w-6 h-6 ${n <= rating ? "text-amber-500 fill-amber-400" : "text-zinc-300"}`} />
              </button>
            ))}
          </div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="How was it?" data-testid="review-text-input"
            className="w-full border border-zinc-300 px-3 py-2 text-sm" />
          <button onClick={submit} data-testid="review-submit-btn" className="mt-2 bg-emerald-700 text-white px-4 py-2 text-sm font-700 hover:bg-emerald-600 transition-colors">Post review</button>
        </div>
      ) : <p className="text-sm text-muted-foreground mb-4"><Link to="/auth" className="text-primary underline">Sign in</Link> to review this business.</p>}
      <div className="space-y-3">
        {biz.reviews.map((r) => (
          <div key={r.id} className="border border-zinc-300 bg-white p-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-700">{r.user_name}</span>
              <span className="flex">{[...Array(5)].map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "text-amber-500 fill-amber-400" : "text-zinc-300"}`} />)}</span>
            </div>
            {r.text && <p className="text-sm text-zinc-700 mt-1">{r.text}</p>}
          </div>
        ))}
        {!biz.reviews.length && <p className="text-sm text-muted-foreground">No reviews yet — be the first.</p>}
      </div>
    </div>
  );
}

export default function BusinessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const load = () => api.get(`/marketplace/businesses/${id}`).then((r) => setB(r.data)).catch(() => setB(false));
  useEffect(() => { window.scrollTo(0, 0); setB(null); load(); }, [id]); // eslint-disable-line

  if (b === null) return <Spinner />;
  if (b === false) return <div className="max-w-3xl mx-auto px-5 py-20 text-center text-muted-foreground">Business not found.</div>;

  const theme = vibrantTheme(`biz-${b.id}`);

  return (
    <div data-testid="business-detail-page">
      <div className="text-white" style={{ backgroundColor: theme.bg }}>
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm font-700 text-white/85 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
          <div className="text-xs font-mono uppercase tracking-widest text-white/80">{b.subcategory || b.category} · {b.airport_iata}</div>
          <h1 className="font-heading font-900 text-3xl sm:text-4xl mt-1 flex items-center gap-3">
            {b.name} {b.verified && <span className="inline-flex items-center gap-1 bg-white/15 px-3 py-1 text-sm font-700"><BadgeCheck className="w-4 h-4" /> Verified</span>}
          </h1>
          <div className="flex items-center gap-1.5 mt-2 text-white/90"><MapPin className="w-4 h-4" />
            <Link to={`/airports/${b.airport_iata}?tab=shops`} className="underline hover:text-white">{b.airport_name}</Link>
            <span>→ {locationTrail(b)}</span>
            {b.security_side && <span className="bg-white/15 px-2 py-0.5 text-xs font-700 ml-1">{b.security_side === "after" ? "After security" : "Before security"}</span>}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {b.description && <p className="text-lg text-zinc-800">{b.description}</p>}

          <MinisiteView minisite={b.minisite} />

          {b.deals?.length > 0 && (
            <div className="space-y-2" data-testid="business-deals">
              {b.deals.map((d) => (
                <div key={d.id} className="border border-emerald-300 bg-emerald-50 px-4 py-3">
                  <div className="flex items-center gap-2 text-emerald-700 text-xs font-mono uppercase tracking-widest"><Tag className="w-4 h-4" /> Deal{d.valid_until ? ` · until ${d.valid_until}` : ""}</div>
                  <div className="font-heading font-900 text-lg">{d.title}</div>
                  {d.description && <p className="text-sm text-zinc-700">{d.description}</p>}
                </div>
              ))}
            </div>
          )}

          <Reviews biz={b} reload={load} />

          <div data-testid="business-photos">
            <h2 className="font-heading font-900 text-xl mb-3 flex items-center gap-2"><Camera className="w-5 h-5 text-primary" /> Photos</h2>
            <PhotoGallery entityType="business" entityId={b.id} />
          </div>

          {b.nearby?.length > 0 && (
            <div>
              <h2 className="font-heading font-900 text-xl mb-3 flex items-center gap-2"><Store className="w-5 h-5 text-primary" /> Nearby at {b.airport_iata}</h2>
              <div className="grid sm:grid-cols-2 gap-3">{b.nearby.map((n) => <BusinessCard key={n.id} b={n} />)}</div>
            </div>
          )}
        </div>

        <div className="space-y-5">
          <div className="border border-zinc-300 bg-white p-5 space-y-3 text-sm" data-testid="business-info-panel">
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-zinc-500 shrink-0" />{b.hours || "Hours not yet verified"}</div>
            <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-zinc-500 shrink-0" />{b.phone || "Phone not yet verified"}</div>
            <div className="flex items-center gap-2"><UtensilsCrossed className="w-4 h-4 text-zinc-500 shrink-0" />
              {b.menu_url ? <a href={b.menu_url} target="_blank" rel="noreferrer" className="text-primary underline" data-testid="business-menu-link">View menu / catalog</a> : "Menu not yet published"}
            </div>
            <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-zinc-500 shrink-0" />
              {b.website ? <a href={b.website} target="_blank" rel="noreferrer" className="text-primary underline break-all">{b.website.replace(/^https?:\/\//, "")}</a> : "Website not yet verified"}
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {b.local_brand && <Pill tone="green">Local favorite</Pill>}
              {b.price_level && <Pill>{"$".repeat(b.price_level)}</Pill>}
              <Pill tone={b.status === "open" ? "green" : "amber"}>{b.status}</Pill>
            </div>
            <p className="text-xs text-muted-foreground pt-1">{b.source || "Community sourced"}. Details change frequently — the airport directory is the final word.</p>
          </div>

          {!b.verified && (
            <div className="border border-emerald-800 bg-emerald-800 text-white p-5" data-testid="claim-panel">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-400"><BadgeCheck className="w-4 h-4" /> Own this business?</div>
              <div className="font-heading font-900 text-xl mt-1">Claim this business</div>
              <p className="text-sm text-zinc-300 mt-1">Verify ownership to publish your own mini page — photos, menu, opening hours, contact info and traveler deals — free.</p>
              {user ? (
                claiming ? <div className="bg-white text-zinc-900 p-3 mt-3"><ClaimForm bizId={b.id} onDone={() => setClaiming(false)} /></div>
                  : <button onClick={() => setClaiming(true)} data-testid="claim-open-btn" className="mt-3 bg-emerald-500 text-zinc-950 px-4 py-2 text-sm font-700 hover:bg-emerald-400 transition-colors">Claim this business</button>
              ) : <Link to="/auth" className="inline-block mt-3 bg-emerald-500 text-zinc-950 px-4 py-2 text-sm font-700 hover:bg-emerald-400 transition-colors">Sign in to claim</Link>}
            </div>
          )}

          {b.brand_locations?.length > 0 && (
            <div className="border border-zinc-300 bg-white p-5" data-testid="brand-locations">
              <div className="flex items-center gap-2 font-heading font-900"><Building2 className="w-4 h-4 text-primary" /> {b.brand} at other airports</div>
              <div className="mt-2 space-y-1.5">
                {b.brand_locations.map((l) => (
                  <Link key={l.id} to={`/business/${l.id}`} className="flex items-center justify-between text-sm hover:text-primary transition-colors">
                    <span className="truncate">{l.airport_name}</span><span className="font-mono text-xs text-primary">{l.airport_iata}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
